import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Projects from './pages/Projects';
import ProjectPage from './pages/ProjectPage';
import Podcasts from './pages/Podcasts';
import Support from './pages/Support';
import Privacy from './pages/Privacy';
import ApresNotePage from './pages/ApresNotePage';
import { AuthProvider } from './contexts/AuthContext';
import { initGA4 } from './utils/analytics';
import { usePageTracking } from './hooks/usePageTracking';

function AppContent() {
  // Track page views on route changes
  usePageTracking();

  return (
    <div className="App">
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:projectSlug" element={<Projects />} />
          {/* Project sub-pages (privacy, support, etc.) */}
          <Route path="/projects/:projectSlug/:page" element={<ProjectPage />} />
          <Route path="/podcasts" element={<Podcasts />} />
          <Route path="/support" element={<Support />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/apresnote/privacy" element={<ApresNotePage page="privacy" />} />
          <Route path="/apresnote/terms" element={<ApresNotePage page="terms" />} />
          <Route path="/apresnote/support" element={<ApresNotePage page="support" />} />
          {/* Legacy Clipz routes - redirect to new paths */}
          <Route path="/clipz" element={<Navigate to="/projects/clipz" replace />} />
          <Route path="/clipz/privacy" element={<Navigate to="/projects/clipz/privacy" replace />} />
          <Route path="/clipz/support" element={<Navigate to="/projects/clipz/support" replace />} />
          <Route path="/clipz/terms" element={<Navigate to="/projects/clipz/terms" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  // Initialize GA4 once on app load
  useEffect(() => {
    initGA4();

    // Log OAuth configuration for debugging
    console.log('[App] Google OAuth Configuration:');
    console.log('[App]   Client ID:', googleClientId ? `${googleClientId.substring(0, 20)}...` : 'NOT SET');
    console.log('[App]   Current Origin:', window.location.origin);
    console.log('[App]   Expected authorized origin: https://apresai.dev');
  }, [googleClientId]);

  if (!googleClientId) {
    console.error('[App] Google Client ID is not configured');
    return <div>Google Client ID is not configured. Please set VITE_GOOGLE_CLIENT_ID environment variable.</div>;
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <AppContent />
        </Router>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;